import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      <Helmet>
        <title>About — Practicio | Practice Management for Design Professionals</title>
        <meta name="description" content="Learn about Practicio — a practice management platform built for architects and interior designers in India. Bringing structure to approvals, BOQs, and billing." />
        <link rel="canonical" href="https://practicio.io/about" />
      </Helmet>
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors mb-10" data-testid="about-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-slate-700 mb-6" data-testid="about-heading">About Practicio</h1>

          <div className="space-y-6 text-stone-600 leading-relaxed">
            <p className="text-lg">
              Practicio is a practice management platform built specifically for architects and interior designers in India.
            </p>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">Why we exist</h2>
              <p>
                Design professionals in India run their practices on WhatsApp threads, Excel BOQs, and verbal approvals. It works — until a client disputes a decision from three months ago, or a scope change quietly inflates the project by 40%, or a payment conversation turns personal because there's no documented milestone.
              </p>
              <p>
                We built Practicio because these problems aren't about discipline or effort. They're about not having the right structure. Design professionals deserve tools that match the complexity and professionalism of their work.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">What we do</h2>
              <p>
                Practicio brings structure to the parts of your practice that are hardest to manage: client approvals, BOQ revisions, scope changes, and billing milestones. Everything is tracked, timestamped, and linked — so you always have a clear record of what was agreed, when, and by whom.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">Who it's for</h2>
              <p>
                We serve architects, interior designers, design consultants, and small studios across India — from solo practitioners in Pune to multi-person studios in Mumbai and Bengaluru. Whether you manage 3 projects or 30, Practicio scales with your practice.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">Our approach</h2>
              <p>
                We believe in simplicity. Practicio isn't a CRM, a task manager, or a project management suite stuffed with features you'll never use. It does one thing well: gives you a professional, structured way to run projects from the first client meeting to the final invoice.
              </p>
              <p>
                No steep learning curve. No training decks. Most studios are running their first project on it within a day.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">Get in touch</h2>
              <p>
                We'd love to hear from you. Whether you have questions, feedback, or just want to learn more, reach out at{' '}
                <a href="mailto:hello@practicio.io" className="text-slate-600 hover:text-slate-700 font-medium underline underline-offset-2">
                  hello@practicio.io
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
